// API URLs
export const API_URL = process.env.REACT_APP_API_URL
export const BASE_NAME_FRONTEND = process.env.REACT_APP_BASE_NAME_FRONTEND || ''; // Default to empty if env var is not set

const ALGO_ENGINE_API_URL = process.env.REACT_APP_ALGO_ENGINE_API_URL
const IR_API_URL = process.env.REACT_APP_IR_API_URL
const RESEARCH_ADVICE_API_URL = process.env.REACT_APP_RESEARCH_ADVICE_API_URL || 'http://localhost:8000/api'; // Default to local if env var is not set
const ANALYTICS_API_URL = process.env.REACT_APP_ANALYTICS_API_URL

const ALGO_ENGINE_NON_COLO_API_URL = process.env.REACT_APP_ALGO_ENGINE_API_URL_NON_COLO || ALGO_ENGINE_API_URL || '';
const ALGO_ENGINE_EMKAY_COLO_NSE_API_URL = process.env.REACT_APP_ALGO_ENGINE_API_URL_EMKAY_COLO_NSE || ALGO_ENGINE_NON_COLO_API_URL;
const ALGO_ENGINE_EMKAY_COLO_BSE_API_URL = process.env.REACT_APP_ALGO_ENGINE_API_URL_EMKAY_COLO_BSE || ALGO_ENGINE_NON_COLO_API_URL;

export const AlgoEngineDestinations = {
  NON_COLO: 'NON_COLO',
  EMKAY_COLO_NSE: 'EMKAY_COLO_NSE',
  EMKAY_COLO_BSE: 'EMKAY_COLO_BSE',
} as const;

export type AlgoEngineDestination = typeof AlgoEngineDestinations[keyof typeof AlgoEngineDestinations];

export interface AlgoEngineRouteSet {
  LOGIN_ROUTE: string;
  GET_CLIENT_IDS_ROUTE: string;
  ORDER_FILE_UPLOAD_PARSE_ORDERS_ROUTE: string;
  ORDER_UPLOAD_PARSE_ORDERS_ROUTE: string;
  SYNTH_FUT_PARSE_ORDER_ROUTE: string;
  GET_FUT_SYMBOLS_ROUTE: string;
  GET_OPT_SYMBOLS_ROUTE: string;
  GET_LTP_ROUTE: string;
  SEND_ORDERS_ROUTE: string;
  SYNTH_FUT_PLACE_ORDER_ROUTE: string;
  SYNTH_FUT_ORDER_STATUS_ROUTE: string;
  SYNTH_FUT_ORDER_STATUS_MIN_ROUTE: string;
  SYNTH_FUT_MODIFY_STRIKE_ROUTE: string;
  DRAFT_ORDERS_ROUTE: string;
  CLEAR_DRAFT_ORDERS_ROUTE: string;
  GET_DRAFT_ORDERS_ROUTE: string;
  GET_ORDER_REQ_ROUTE: string;
  GET_ORDERS_ROUTE: string;
  GET_ORDERS_STATUS_ROUTE: string;
  GET_ORDERS_STATUS_ALL_ROUTE: string;
  GET_CLIENT_POSITION: string;
  STOP_ORDERS_ROUTE: string;
  DOWNLOAD_ORDERS_STATUS_FILE_ROUTE: string;
  GET_ALGO_ENGINE_STATUS: string;
  MODIFY_ORDERS_ROUTE: string;
  GET_PAIRS_ORDERS_ROUTE: string;
  GET_PAIRS_ORDERS_STATUS: string;
  PARSE_PAIRS_ORDER: string;
  CREATE_PAIRS_ORDER: string;
  PAIRS_STOP_ORDERS_ROUTE: string;
}

export const DEFAULT_ALGO_ENGINE_DESTINATION: AlgoEngineDestination = AlgoEngineDestinations.NON_COLO;

export const isAlgoEngineDestination = (value: unknown): value is AlgoEngineDestination => {
  return typeof value === 'string' && (Object.values(AlgoEngineDestinations) as string[]).includes(value);
};

export const getAlgoEngineBaseUrl = (destination: AlgoEngineDestination): string => {
  switch (destination) {
    case AlgoEngineDestinations.EMKAY_COLO_NSE:
      return ALGO_ENGINE_EMKAY_COLO_NSE_API_URL;
    case AlgoEngineDestinations.EMKAY_COLO_BSE:
      return ALGO_ENGINE_EMKAY_COLO_BSE_API_URL;
    case AlgoEngineDestinations.NON_COLO:
    default:
      return ALGO_ENGINE_NON_COLO_API_URL;
  }
};

export const getAlgoEngineApiRoutes = (destination: AlgoEngineDestination): AlgoEngineRouteSet => {
  const baseUrl = getAlgoEngineBaseUrl(destination);

  return {
    LOGIN_ROUTE: `${baseUrl}/auth/login`,
    GET_CLIENT_IDS_ROUTE: `${baseUrl}/user/get_client_ids`,
    ORDER_FILE_UPLOAD_PARSE_ORDERS_ROUTE: `${baseUrl}/order_req/order_file_upload/parse_orders`,
    ORDER_UPLOAD_PARSE_ORDERS_ROUTE: `${baseUrl}/order_req/new_orders/parse_orders`,
    SYNTH_FUT_PARSE_ORDER_ROUTE: `${baseUrl}/synth_fut/parse_order`,
    GET_FUT_SYMBOLS_ROUTE: `${baseUrl}/snapshot/trading_symbols/fut`,
    GET_OPT_SYMBOLS_ROUTE: `${baseUrl}/snapshot/trading_symbols/opt`,
    GET_LTP_ROUTE: `${baseUrl}/snapshot/ltp`,
    SEND_ORDERS_ROUTE: `${baseUrl}/order_req/new_orders/create_orders`,
    SYNTH_FUT_PLACE_ORDER_ROUTE: `${baseUrl}/synth_fut/place_order`,
    SYNTH_FUT_ORDER_STATUS_ROUTE: `${baseUrl}/synth_fut/order_status`,
    SYNTH_FUT_ORDER_STATUS_MIN_ROUTE: `${baseUrl}/synth_fut/order_status_min`,
    SYNTH_FUT_MODIFY_STRIKE_ROUTE: `${baseUrl}/synth_fut/modify_strike`,
    DRAFT_ORDERS_ROUTE: `${baseUrl}/order_req/new_orders/draft_orders`,
    CLEAR_DRAFT_ORDERS_ROUTE: `${baseUrl}/order_req/clear_draft_orders`,
    GET_DRAFT_ORDERS_ROUTE: `${baseUrl}/order_req/order_drafts`,
    GET_ORDER_REQ_ROUTE: `${baseUrl}/order_req/order_requests`,
    GET_ORDERS_ROUTE: `${baseUrl}/snapshot/parent_orders`,
    GET_ORDERS_STATUS_ROUTE: `${baseUrl}/snapshot/parent_orders_status`,
    GET_ORDERS_STATUS_ALL_ROUTE: `${baseUrl}/snapshot/parent_orders_status_all`,
    GET_CLIENT_POSITION: `${baseUrl}/snapshot/client_positions`,
    STOP_ORDERS_ROUTE: `${baseUrl}/order_req/stop_order`,
    DOWNLOAD_ORDERS_STATUS_FILE_ROUTE: `${baseUrl}/snapshot/order_status_file`,
    GET_ALGO_ENGINE_STATUS: `${baseUrl}/user/get_algoengine_status`,
    MODIFY_ORDERS_ROUTE: `${baseUrl}/order_req/modify_order`,
    GET_PAIRS_ORDERS_ROUTE: `${baseUrl}/snapshot/pair_orders`,
    GET_PAIRS_ORDERS_STATUS: `${baseUrl}/snapshot/pair_order_status`,
    PARSE_PAIRS_ORDER: `${baseUrl}/order_req/new_pair_orders/parse_orders`,
    CREATE_PAIRS_ORDER: `${baseUrl}/order_req/new_pair_orders/create_orders`,
    PAIRS_STOP_ORDERS_ROUTE: `${baseUrl}/order_req/stop_pair_order`,
  };
};

// export const API_URL = 'https://rms.taracapitalpartners.com/rmsapp'; // prod-fpi-rms
// export const API_URL = 'http://localhost:8080/rmswebservice/api';
// export const BASE_NAME_FRONTEND = ''; // prod-fpi-rms

// TEST
// export const API_URL = 'http://172.31.100.108/fpirmstestapi'; // prod-fpi-rms
// export const BASE_NAME_FRONTEND = '/fpirms'; // prod-fpi-rms

// RMS DR
// export const API_URL = 'http://172.16.5.201/rmsapi';
// export const API_URL = 'https://rms-dr.taracapitalpartners.com/rmsapp';
// export const BASE_NAME_FRONTEND = '/fpirms';

// const ALGO_ENGINE_API_URL = 'https://algoengine.taracapitalpartners.com:8080/api'; // prod-algoengine
// const ALGO_ENGINE_API_URL = 'https://sg-algoengine.taracapitalpartners.com/algoengineapi'; // prod-algoengine
// const ALGO_ENGINE_API_URL = 'https://algoengine.taracapitalpartners.com:8080/edco2_api'; // prod-algoengine
// const ALGO_ENGINE_API_URL = 'http://10.0.0.96:8086/api'; // uat-algoengine


// const ALGO_ENGINE_API_URL = 'http://10.0.0.27:8086/api'; // test-algoengine

// const RESEARCH_ADVICE_API_URL = 'http://localhost:8000/api'; // research_advice_webservice
// const RESEARCH_ADVICE_API_URL = 'http://172.31.100.108/researchadviceapi/api'; // research_advice_webservice

// const IR_API_URL = 'https://algoengine.taracapitalpartners.com:8080/tara_ir/v1';
// const IR_API_URL = 'http://10.0.0.157:8989/tara_ir/v1'; // test-ir
// const IR_API_URL = 'http://10.0.0.96:8086/tara_ir/v1';

export const LOGIN_ROUTE = '/Backoffice/Login';
export const CONTRIBUTION_ROUTE = '/Snapshots/Contribution';
export const POSITION_ROUTE = '/Snapshots/Position';
export const FULLBOOK_ROUTE = '/Snapshots/Fullbook';
export const LIQUIDITY_ROUTE = '/Snapshots/Liquidity';
export const SIDE_ROUTE = '/Snapshots/Side';
export const MARKET_CAPITAL_ROUTE = '/Snapshots/MarketCapital';
export const PNL_ROUTE = '/IntradaySnapshot/pnlBenchmark';
export const CATEGORY_ROUTE = '/Snapshots/Category';
export const SECTOR_ROUTE = '/Snapshots/Sector';
export const POSITION_LOOKUP_ROUTE = '/Snapshots/SearchSymbol';
export const MARGIN_ROUTE = '/Snapshots/Margin';
export const FULLBOOK_MARGIN_ROUTE = '/Snapshots/FullbookMarginUtilization';
export const OPTIONS_POSITION_ROUTE = '/Snapshots/OptionPositions';
export const SYMBOLS_SNAPSHOT_ROUTE = '/Snapshots/SymbolSnapshot';
export const RESTRICTED_STOCKS_ROUTE = '/Snapshots/RestrictedStocks';
export const CORPORATE_ACTIONS_BY_SYMBOL_ROUTE = '/Snapshots/CorporateActionsBySymbol';
export const EARNINGS_CALENDAR_ROUTE = '/Snapshots/EarningsCalendar';
export const EARNINGS_CALENDAR_ANALYTICS_ROUTE = '/Snapshots/EarningsCalendarAnalytics';
export const VOL_DATA_ROUTE = '/Snapshots/VolData';

export const ADMIN_DETAILS_ROUTE = '/Admin/AccountDetails';
export const UPDATE_CAPITAL_ROUTE = '/Admin/UpdateCapital';
export const FX_RATE_ROUTE = '/Snapshots/FXRate';
export const INDEX_ROUTE = '/Snapshots/IndexReturns';
export const CASH_AVL_ROUTE = '/Snapshots/CashAvailable';
export const TRADES_BY_ACCOUNT = '/Snapshots/Trades';
export const UNDERLYING_OPTIONS_POSITION_ROUTE = '/Snapshots/AggregatedOptionPositions';
export const UNDERLYING_OPTIONS_POSITION_METRICS_ROUTE = '/IntradaySnapshot/underlyingOptionsPositionMetrics';
export const UNDERLYING_OPTIONS_POSITION_METRICS_BY_SYMBOL_ROUTE = '/IntradaySnapshot/underlyingOptionsPositionMetricsBySymbol';
export const UNDERLYING_OPM_SYMBOLS_BY_ACCOUNT_ROUTE = '/IntradaySnapshot/underlyingOptionsPositionMetricsSymbolsByAccount';
export const STRESS_POSITION_METRICS_AGG_ROUTE_BY_SYMBOL = '/IntradaySnapshot/stressPositionMetricsAggregatedBySymbol';
export const STRESS_POSITION_METRICS_AGG_SNAP_ROUTE_BY_SYMBOL = '/Snapshots/StressPositionMetricsAggregatedSnapshotBySymbol';
export const UPDATED_TIME_ROUTE = '/Snapshots/UpdatedTime';
export const ACCOUNT_STRATEGY_MAPPING_ROUTE = '/Snapshots/AccountStrategyMapping';
export const STRATEGY_OPTIONS_POSITION_ROUTE = '/Snapshots/StrategyOptionPositions';
export const AGGREGATED_STRATEGY_OPTIONS_POSITION_ROUTE = '/Snapshots/AggregatedStrategyOptionPositions';
export const ALL_AGGREGATED_STRATEGY_OPTIONS_POSITION_ROUTE = '/Snapshots/AllAggregatedStrategyOptionPositions';
export const TICKER_SNAPSHOT_ROUTE = '/Snapshots/Tickers';
export const TERM_STRUCTURE_VOL_ROUTE = '/Snapshots/TermStructureVol';
export const PAYOFF_BY_SYMBOL_ROUTE = '/Snapshots/PayoffBySymbol';
export const TRADABLES_ROUTE = '/Snapshots/Tradables';

export const MANAGER_ACCOUNT_DETAILS_ROUTE = '/Admin/ManagerAccountDetails';
export const TRADER_ACCOUNT_DETAILS_ROUTE = '/Admin/TraderAccountDetails';
export const USER_PREF_BY_ACCESS_ROUTE = '/Admin/UserPreferenceByAccess';
export const CREATE_USER_PREF_ACCOUNT = '/Admin/CreateUserPreferenceAccount';
export const DELETE_USER_PREF_ACCOUNT = '/Admin/DeleteUserPreferenceAccount';

// Pair Analysis
export const QUOTES = 'Snapshots/Quotes';
export const PAIR_WISE_ROUTE = '/Snapshots/PairWiseAnalytics';
export const PAIR_WATCHLIST_ADD_ROUTE = '/Pairs/Watchlist/Add';
export const PAIR_WATCHLIST_LIST_ROUTE = '/Pairs/Watchlist/List';
export const PAIR_WATCHLIST_REMOVE_ROUTE = '/Pairs/Watchlist/Remove';
export const PAIR_DEFAULTS_LIST_ROUTE = '/Pairs/Defaults/List';

// Alerts
export const VIEW_ALERT_ROUTE = '/Alerts/View';
export const CREATE_ALERT_ROUTE = '/Alerts/Create';
export const UPDATE_ALERT_ROUTE = '/Alerts/Update';
export const CLOSE_ALERT_ROUTE = '/Alerts/Close';
export const VIEW_CLOSED_ALERT_ROUTE = '/Alerts/ViewClosed';
export const PAIR_CREATE_ALERT_ROUTE = '/Alerts/PairCreate';
export const PAIR_UPDATE_ALERT_ROUTE = '/Alerts/PairUpdate';
export const PAIR_VIEW_ALERT_ROUTE = '/Alerts/PairView';
export const PAIR_CLOSE_ALERT_ROUTE = '/Alerts/PairClose';
export const PAIR_VIEW_CLOSED_ALERT_ROUTE = '/Alerts/PairViewClosed';
export const ALERT_NOTIFICATIONS_ROUTE = '/Alerts/Notifications';
export const ALERT_NOTIFICATIONS_MARK_READ_ROUTE = '/Alerts/NotificationsMarkRead';

// Market color dashboard
export const INDEX_PRICE_DATA_ROUTE = '/Snapshots/IndexPriceData';
export const INTRADAY_INDEX_PRICE_DATA_ROUTE = '/Snapshots/IntradayIndexPriceData';
export const CORPORATE_ACTIONS_ROUTE = '/Snapshots/CorporateActions';
export const EXCHANGE_CIRCULARS_ROUTE = '/Snapshots/ExchangeCirculars';
export const CORPORATE_ANNOUNCEMENTS_ROUTE = '/Snapshots/CorporateAnnouncements';
export const GAINERS_LOSERS_ROUTE = '/Snapshots/GainersLosers';
export const CORRELATION_DATA_ROUTE = '/Snapshots/CorrelationData';
export const OI_SPURTS_ROUTE = '/Snapshots/OiSpurts';
export const AVAILABLE_EXPIRIES_ROUTE = '/Snapshots/AvailableExpiries';
export const FUTURES_PREMIUM_DISCOUNT_ROUTE = '/Snapshots/FuturesPremiumDiscountLive';
export const FUTURES_OI_ROUTE = '/Snapshots/FuturesOiData';
export const CASH_TRADE_DATA_ROUTE = '/Snapshots/CashTradeData';
export const FACTOR_CORRELATION_DATA_ROUTE = '/Snapshots/FactorCorrelationData';
export const FACTOR_CORRELATION_DOC_URL = "https://docs.google.com/document/d/1j6NrFlmIvRxAhwFU7OE8ZPdOwEajmha9dkBhlyBao3Q/edit?usp=sharing";

export const VOL_ACCOUNTS_SUMMARY_ROUTE = '/Snapshots/VolAccountsSummary';
export const AGGREGATED_POSITIONS_BY_SYMBOL_ROUTE = '/Snapshots/AggregatedPositionsBySymbol';
export const FNO_TRANSITION_ROUTE = '/Snapshots/FnOTransition';
export const NSE_PRICE_OI_CHART_DATA_ROUTE = '/Snapshots/NsePriceOiChartData';
export const INDEX_CONSTITUENTS_DATA_ROUTE = '/Snapshots/IndexConstituents';
export const SYNTHETIC_POSITIONS_ROUTE = '/Snapshots/SyntheticPositions';
export const SYNTHETIC_GROUP_ROUTE = '/Snapshots/SyntheticGroup';

export const SYMBOL_MAPS_LIST = 'Admin/GetAllSymbolMaps'
export const CREATE_SYMBOL_MAPS = 'Admin/CreateSymbolMap'
export const UPDATE_SYMBOLS = 'Admin/UpdateSymbolMap'

export class AlgoEngineApiRoutes {
  public static readonly LOGIN_ROUTE = `${ALGO_ENGINE_API_URL}/auth/login`;
  public static readonly GET_CLIENT_IDS_ROUTE = `${ALGO_ENGINE_API_URL}/user/get_client_ids`;

  public static readonly ORDER_FILE_UPLOAD_PARSE_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/order_file_upload/parse_orders`;
  public static readonly ORDER_UPLOAD_PARSE_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/new_orders/parse_orders`;
  public static readonly SYNTH_FUT_PARSE_ORDER_ROUTE = `${ALGO_ENGINE_API_URL}/synth_fut/parse_order`;
  public static readonly GET_FUT_SYMBOLS_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/trading_symbols/fut`;
  public static readonly GET_OPT_SYMBOLS_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/trading_symbols/opt`;
  public static readonly GET_LTP_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/ltp`;

  public static readonly SEND_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/new_orders/create_orders`;
  public static readonly SYNTH_FUT_PLACE_ORDER_ROUTE = `${ALGO_ENGINE_API_URL}/synth_fut/place_order`;
  public static readonly SYNTH_FUT_ORDER_STATUS_ROUTE = `${ALGO_ENGINE_API_URL}/synth_fut/order_status`;
  public static readonly SYNTH_FUT_ORDER_STATUS_MIN_ROUTE = `${ALGO_ENGINE_API_URL}/synth_fut/order_status_min`;
  public static readonly SYNTH_FUT_MODIFY_STRIKE_ROUTE = `${ALGO_ENGINE_API_URL}/synth_fut/modify_strike`;
  public static readonly DRAFT_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/new_orders/draft_orders`;
  public static readonly CLEAR_DRAFT_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/clear_draft_orders`;

  public static readonly GET_DRAFT_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/order_drafts`;

  public static readonly GET_ORDER_REQ_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/order_requests`;

  public static readonly GET_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/parent_orders`;

  public static readonly GET_ORDERS_STATUS_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/parent_orders_status`;

  public static readonly GET_ORDERS_STATUS_ALL_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/parent_orders_status_all`;

  public static readonly GET_CLIENT_POSITION = `${ALGO_ENGINE_API_URL}/snapshot/client_positions`;

  public static readonly STOP_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/stop_order`;

  public static readonly DOWNLOAD_ORDERS_STATUS_FILE_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/order_status_file`;

  public static readonly GET_ALGO_ENGINE_STATUS = `${ALGO_ENGINE_API_URL}/user/get_algoengine_status`;

  public static readonly MODIFY_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/modify_order`;

  public static readonly GET_PAIRS_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/snapshot/pair_orders`;

  public static readonly GET_PAIRS_ORDERS_STATUS = `${ALGO_ENGINE_API_URL}/snapshot/pair_order_status`;

  public static readonly PARSE_PAIRS_ORDER = `${ALGO_ENGINE_API_URL}/order_req/new_pair_orders/parse_orders`;

  public static readonly CREATE_PAIRS_ORDER = `${ALGO_ENGINE_API_URL}/order_req/new_pair_orders/create_orders`;

  public static readonly PAIRS_STOP_ORDERS_ROUTE = `${ALGO_ENGINE_API_URL}/order_req/stop_pair_order`;

};

export class ResearchAdviceApiRoutes {

  public static readonly LOGIN_ROUTE = `${RESEARCH_ADVICE_API_URL}/v1/auth/login`;

  // Accounts
  public static readonly GET_ACCOUNTS_ROUTE = `${RESEARCH_ADVICE_API_URL}/v1/account`;

  // Broker
  public static readonly GET_BROKERS_ROUTE = `${RESEARCH_ADVICE_API_URL}/v1/broker`;

  // Research Advice
  public static readonly GET_RESEARCH_ADVICE = `${RESEARCH_ADVICE_API_URL}/v1/research_advice`;

};

export class IRApiRoutes {

  // Login route
  public static readonly API_URL = IR_API_URL;

  // Login route
  public static readonly LOGIN_ROUTE = `${IR_API_URL}/login`;

  // Webhook
  public static readonly WEBHOOK_ROUTE = "wss://algoengine.taracapitalpartners.com:8080/ws_tara_ir/v1";

  // Get trade summary
  public static readonly GET_TRADE_SUMMARY = `${IR_API_URL}/snapshot/trad_summ`;

  // Get target status
  public static readonly GET_TARGET_STATUS = `${IR_API_URL}/snapshot/tgt_status`;

  // All instruments
  public static readonly GET_ALLOWED_INSTRUMENTS = `${IR_API_URL}/snapshot/all_instruments`;

  // Broker algos
  public static readonly GET_BROKER_ALGOS = `${IR_API_URL}/snapshot/broker_algos_mapping`;

  // Account Ids
  public static readonly GET_ALLOWED_ACC_IDS = `${IR_API_URL}/snapshot/allowed_acc_ids`;

  // Order Advices
  public static readonly GET_ORDER_ADVICES = `${IR_API_URL}/snapshot/order_advices`;

  // Get staged orders
  public static readonly GET_STAGED_ORDER = `${IR_API_URL}/orders/staged_orders`;

  // Remove staged orders
  public static readonly REMOVE_STAGED_ORDER = `${IR_API_URL}/orders/staged_orders/remove`;

  // Modify staged order
  public static readonly MODIFY_STAGED_ORDER = `${IR_API_URL}/order/staged_order/modify`;

  // Stage Order
  public static readonly STAGE_ORDER = `${IR_API_URL}/new_orders/parse_algo_advices`;

  // Place order
  public static readonly PLACE_ORDER = `${IR_API_URL}/new_orders/place_algo_advices`;

  // Modify Order
  public static readonly MODIFY_ORDER = `${IR_API_URL}/order/modify`;

  // Modify Order
  public static readonly INSTRUCTION_ORDER = `${IR_API_URL}/order/instruction`;

  // Terminate order
  public static readonly TERMINATE_ORDER = `${IR_API_URL}/terminate_targets`;

  // upload files
  public static readonly UPLOAD_ORDER_FILE = `${IR_API_URL}/upload/algo_advices_file`;

  // parse file
  public static readonly PARSE_UPLOADED_ORDER_FILE = `${IR_API_URL}/upload/algo_advices_parse_file`;

  //child orders 
  public static readonly CHILD_ORDERS = `${IR_API_URL}/snapshot/child_order_updates`;
};

export class AnalyticsApiRoutes {
  public static readonly DAILY_STATS_ROUTE = `${ANALYTICS_API_URL}/daily_stats`;
  public static readonly LIVE_STATS_ROUTE = `${ANALYTICS_API_URL}/live_stats`;
  public static readonly COMBINED_VOL_CURVE_ROUTE = `${ANALYTICS_API_URL}/combined_vol_curve`;
};
